/**
 * Plays the Learning Room's loop (template-parts/learning-room/preview-video.php)
 * while it is on screen and the tab is visible, and lets the visitor pause it.
 *
 * The video gets its source only when the figure first comes into view, and
 * only one: the phone file for a narrow figure, the desktop file otherwise.
 * Under reduced motion the still stays until the visitor presses play. When
 * the browser refuses to play, or the file fails, the still stays too: the
 * first case leaves the play button, the second hides it.
 */
const PLAYING_CLASS = 'is-playing';

// The figure width up to which the phone file (768 × 432) is sharp enough.
const MOBILE_MAX_WIDTH = 768;

const reducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' );

/**
 * @param {HTMLElement} figure The `[data-r2o-lr-preview]` figure.
 */
function setUp( figure ) {
	const video = figure.querySelector( 'video' );
	const toggle = figure.querySelector( '.r2o-lr-preview-toggle' );

	if ( ! video || ! toggle || typeof video.play !== 'function' ) {
		return;
	}

	let onScreen = false;
	let paused = reducedMotion.matches;
	let failed = false;

	const showState = () => {
		const label = paused
			? toggle.dataset.labelPlay
			: toggle.dataset.labelPause;

		toggle.setAttribute( 'aria-label', label );
		toggle.classList.toggle( 'is-paused', paused );
	};

	const update = () => {
		if ( failed || paused || ! onScreen || document.hidden ) {
			video.pause();
			return;
		}

		if ( ! video.src ) {
			const narrow = figure.clientWidth <= MOBILE_MAX_WIDTH;
			video.src = narrow
				? figure.dataset.srcMobile
				: figure.dataset.srcDesktop;
		}

		video.play().catch( ( error ) => {
			// A pause() before the play started; nothing went wrong.
			if ( error.name === 'AbortError' ) {
				return;
			}

			// Autoplay refused (data saver, a strict in-app browser): the
			// still stays and the button offers to start it by hand.
			paused = true;
			showState();
		} );
	};

	video.addEventListener( 'playing', () =>
		figure.classList.add( PLAYING_CLASS )
	);

	video.addEventListener( 'error', () => {
		failed = true;
		figure.classList.remove( PLAYING_CLASS );
		toggle.hidden = true;
	} );

	toggle.addEventListener( 'click', () => {
		paused = ! paused;
		showState();
		update();
	} );

	new window.IntersectionObserver(
		( [ entry ] ) => {
			onScreen = entry.isIntersecting;
			update();
		},
		{ threshold: 0.1 }
	).observe( figure );

	document.addEventListener( 'visibilitychange', update );

	toggle.hidden = false;
	showState();
}

if ( 'IntersectionObserver' in window ) {
	document.querySelectorAll( '[data-r2o-lr-preview]' ).forEach( setUp );
}
